// src/components/EstudianteDetalles.js
import React, { useState, useEffect } from "react";
import { useNavigate, useParams } from "react-router-dom";
import {
  Container,
  Card,
  Button,
  Row,
  Col,
  Alert,
  Spinner,
  Table,
  Modal,
  Form,
} from "react-bootstrap";
import {
  FaPen,
  FaCheckCircle,
  FaTimesCircle,
  FaListAlt,
  FaExchangeAlt,
} from "react-icons/fa";
import GenerarCredencial from "./GenerarCredencial";
import ImageUploader from "./ImageUploader";

const API_URL = "https://mixta39.dev/phpapi/api/estudiantes.php"; // ajústalo si es necesario
const UPLOADS_URL = "/images/students/";
const DEFAULT_IMAGE = "/images/default-profile.png";

const EstudianteDetalles = () => {
  const navigate = useNavigate();
  const { id } = useParams();

  const [estudiante, setEstudiante] = useState(null);
  const [loading, setLoading] = useState(true);
  const [message, setMessage] = useState(null); // { text, type }
  const [habilitado, setHabilitado] = useState(false);
  const [imagenPerfil, setImagenPerfil] = useState(DEFAULT_IMAGE);

  // Edit modal
  const [showModal, setShowModal] = useState(false);
  const [formData, setFormData] = useState({
    Nombre: "",
    ApellidoPaterno: "",
    ApellidoMaterno: "",
    FechaNacimiento: "",
    CURP: "",
    TelefonoDeContacto: "",
    Genero: "",
    Direccion: "",
    Telefono: "",
    Email: "",
    Edad: "",
    Matricula: "",
    Grado: "",
    Grupo: "",
    Turno: "",
    Activo: true,
    UsuarioCreadorID: "",
    Fotografia: null,
  });
  const [isSaving, setIsSaving] = useState(false);

  // Transfer modal
  const [showTransferModal, setShowTransferModal] = useState(false);
  const [transferData, setTransferData] = useState({ grado: "", grupo: "", turno: "" });
  const [isTransferring, setIsTransferring] = useState(false);

  // ancho uniforme para botones
  const buttonStyle = { minWidth: "150px" };

  // Verificar token (redirigir si no hay)
  useEffect(() => {
    const token = localStorage.getItem("user");
    if (!token) navigate("/login");
  }, [navigate]);

  // Cargar estudiante
  useEffect(() => {
    reloadEstudiante();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [id]);

  const reloadEstudiante = async () => {
    setLoading(true);
    try {
      const res = await fetch(`${API_URL}?EstudianteID=${id}`);
      if (!res.ok) throw new Error("Error al obtener estudiante");
      const data = await res.json();
      if (!data) {
        setMessage({ text: "Estudiante no encontrado.", type: "danger" });
        setEstudiante(null);
      } else {
        setEstudiante(data);
        setImagenPerfil(data.Fotografia ? UPLOADS_URL + data.Fotografia : DEFAULT_IMAGE);
        setHabilitado(!!data.Activo);
        setFormData({
          Nombre: data.Nombre || "",
          ApellidoPaterno: data.ApellidoPaterno || "",
          ApellidoMaterno: data.ApellidoMaterno || "",
          FechaNacimiento: data.FechaNacimiento || "",
          CURP: data.CURP || "",
          TelefonoDeContacto: data.TelefonoDeContacto || "",
          Genero: data.Genero || "",
          Direccion: data.Direccion || "",
          Telefono: data.Telefono || "",
          Email: data.Email || "",
          Edad: data.Edad || "",
          Matricula: data.Matricula || "",
          Grado: data.Grado || "",
          Grupo: data.Grupo || "",
          Turno: data.Turno || "",
          Activo: data.Activo ?? true,
          UsuarioCreadorID: data.UsuarioCreadorID || "",
          Fotografia: data.Fotografia || null,
        });
      }
    } catch (err) {
      console.error(err);
      setMessage({ text: "Hubo un error al obtener los detalles del estudiante.", type: "danger" });
    } finally {
      setLoading(false);
      if (message) setTimeout(() => setMessage(null), 3500);
    }
  };

  // ImageUploader debe devolver solo el nombre del archivo (ej: foto.jpg)
  const updateStudentImage = async (newImageFileName) => {
    try {
      const updatedData = { ...formData, Fotografia: newImageFileName };
      const res = await fetch(`${API_URL}?EstudianteID=${id}`, {
        method: "PUT",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify(updatedData),
      });
      if (!res.ok) throw new Error("Error al actualizar imagen");
      const result = await res.json();
      if (result.success) {
        setImagenPerfil(UPLOADS_URL + newImageFileName);
        setMessage({ text: "Imagen actualizada correctamente.", type: "success" });
        setTimeout(() => setMessage(null), 3000);
        await reloadEstudiante();
      } else {
        setMessage({ text: "No se pudo actualizar la imagen.", type: "danger" });
      }
    } catch (err) {
      console.error(err);
      setMessage({ text: "Error al actualizar la imagen del estudiante.", type: "danger" });
    }
  };

  // Toggle habilitado
  const toggleHabilitado = async () => {
    try {
      const fotografiaNombre = formData.Fotografia || (imagenPerfil !== DEFAULT_IMAGE ? imagenPerfil.replace(UPLOADS_URL, "") : null);
      const updatedData = { ...formData, Activo: !habilitado, Fotografia: fotografiaNombre };

      const res = await fetch(`${API_URL}?EstudianteID=${id}`, {
        method: "PUT",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify(updatedData),
      });
      if (!res.ok) throw new Error("Error al actualizar estado");
      const result = await res.json();
      if (result.success) {
        setHabilitado(!habilitado);
        setMessage({ text: "Estado actualizado correctamente.", type: "success" });
        setTimeout(() => setMessage(null), 3000);
      } else {
        setMessage({ text: "Hubo un error al actualizar el estado.", type: "danger" });
      }
    } catch (err) {
      console.error(err);
      setMessage({ text: "Hubo un error al actualizar el estado del estudiante.", type: "danger" });
    }
  };

  // Cambios en modal editar
  const handleInputChange = (e) => {
    const { name, value } = e.target;
    setMessage(null);
    setFormData(prev => ({ ...prev, [name]: value }));
  };

  // Guardar cambios (editar)
  const handleSaveChanges = async () => {
    if (!estudiante) {
      setMessage({ text: "No hay estudiante cargado.", type: "danger" });
      return;
    }

    const hasChanges = Object.keys(formData).some(
      key => String(formData[key] ?? "") !== String(estudiante[key] ?? "")
    );

    if (!hasChanges) {
      setShowModal(false);
      setMessage({ text: "No hubo cambios por guardar.", type: "info" });
      setTimeout(() => setMessage(null), 2500);
      return;
    }

    try {
      setIsSaving(true);
      const fotografiaNombre = formData.Fotografia || (imagenPerfil !== DEFAULT_IMAGE ? imagenPerfil.replace(UPLOADS_URL, "") : null);
      const updatedData = { ...formData, Fotografia: fotografiaNombre };

      const res = await fetch(`${API_URL}?EstudianteID=${id}`, {
        method: "PUT",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify(updatedData),
      });
      if (!res.ok) throw new Error("Error al guardar cambios");
      const result = await res.json();
      if (result.success) {
        setShowModal(false);
        setMessage({ text: "Datos actualizados correctamente.", type: "success" });
        setTimeout(() => setMessage(null), 3000);
        await reloadEstudiante();
      } else {
        setMessage({ text: "Hubo un error al actualizar los datos.", type: "danger" });
      }
    } catch (err) {
      console.error(err);
      setMessage({ text: "Hubo un error al actualizar los datos.", type: "danger" });
    } finally {
      setIsSaving(false);
    }
  };

  // Transferencia: manejo de cambios
  const handleTransferChange = (e) => {
    const { name, value } = e.target;
    setTransferData(prev => ({ ...prev, [name]: value }));
  };

  // Guardar transferencia
  const handleTransferSubmit = async () => {
    if (!transferData.grado || !transferData.grupo || !transferData.turno) {
      setMessage({ text: "Selecciona Grado, Grupo y Turno.", type: "danger" });
      setTimeout(() => setMessage(null), 3000);
      return;
    }

    try {
      setIsTransferring(true);
      const fotografiaNombre = formData.Fotografia || (imagenPerfil !== DEFAULT_IMAGE ? imagenPerfil.replace(UPLOADS_URL, "") : null);

      const updatedData = {
        ...formData,
        Grado: transferData.grado,
        Grupo: transferData.grupo,
        Turno: transferData.turno,
        Fotografia: fotografiaNombre,
      };

      const res = await fetch(`${API_URL}?EstudianteID=${id}`, {
        method: "PUT",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify(updatedData),
      });
      if (!res.ok) throw new Error("Error al transferir alumno");
      const result = await res.json();
      if (result.success) {
        setShowTransferModal(false);
        setMessage({ text: "Alumno transferido correctamente.", type: "success" });
        setTimeout(() => setMessage(null), 3000);
        await reloadEstudiante();
      } else {
        setMessage({ text: "No se pudo transferir al alumno.", type: "danger" });
      }
    } catch (err) {
      console.error(err);
      setMessage({ text: "Hubo un error al transferir al alumno.", type: "danger" });
    } finally {
      setIsTransferring(false);
    }
  };

  if (loading) {
    return (
      <Container className="mt-5 text-center">
        <Spinner animation="border" />
        <p>Cargando detalles del estudiante...</p>
      </Container>
    );
  }

  if (!estudiante) {
    return (
      <Container className="mt-5">
        <Alert variant="danger">{message?.text || "Estudiante no encontrado."}</Alert>
      </Container>
    );
  }

  const {
    Nombre,
    ApellidoPaterno,
    ApellidoMaterno,
    Edad,
    Grado,
    Grupo,
    Turno,
    CURP,
    Email,
    TelefonoDeContacto,
    Direccion,
    FechaRegistro,
  } = estudiante;

  return (
    <Container className="mt-5">
      <Row>
        {message && (
          <Alert variant={message.type} className="d-flex align-items-center w-100">
            {message.type === "success" ? <FaCheckCircle className="me-2" /> : <FaTimesCircle className="me-2" />}
            {message.text}
          </Alert>
        )}
      </Row>

      <Row>
        {/* Fotografía */}
        <Col md={4} className="mb-4">
          <Card className="shadow">
            <Card.Body className="text-center">
              <div className="profile-img">
                <img
                  src={imagenPerfil}
                  alt="Perfil"
                  className="rounded-circle"
                  style={{ width: "200px", height: "200px", objectFit: "cover" }}
                  onError={() => setImagenPerfil(DEFAULT_IMAGE)}
                />
              </div>
              <h4 className="mt-3">{Nombre} {ApellidoPaterno} {ApellidoMaterno}</h4>
              <p className="text-muted"><strong>{Grado} - {Grupo}</strong></p>
              <div className="d-flex justify-content-center mt-2">
                {/* Envolvemos el GenerarCredencial para forzar minWidth */}
                <div style={{ ...buttonStyle }} className="me-2">
                  <GenerarCredencial estudiante={estudiante} imagenPerfil={imagenPerfil} />
                </div>
              </div>
              <div className="mt-2">
                <ImageUploader onImageUploaded={updateStudentImage} id={id} />
              </div>
            </Card.Body>
          </Card>
        </Col>

        {/* Detalles y acciones */}
        <Col md={8}>
          <Card className="shadow">
            <Card.Body>
              <h5>Detalles del Estudiante</h5>
              <Table striped bordered hover responsive>
                <tbody>
                  <tr><td><strong>Edad</strong></td><td>{Edad || "No disponible"}</td></tr>
                  <tr><td><strong>Turno</strong></td><td>{Turno || "No disponible"}</td></tr>
                  <tr><td><strong>CURP</strong></td><td>{CURP || "No disponible"}</td></tr>
                  <tr><td><strong>Email</strong></td><td>{Email || "No disponible"}</td></tr>
                  <tr><td><strong>Teléfono de Contacto</strong></td><td>{TelefonoDeContacto || "No disponible"}</td></tr>
                  <tr><td><strong>Dirección</strong></td><td>{Direccion || "No disponible"}</td></tr>
                  <tr><td><strong>Fecha de Registro</strong></td><td>{FechaRegistro || "No disponible"}</td></tr>
                </tbody>
              </Table>

              <div className="mb-3 d-flex flex-wrap">
                <Button
                  style={buttonStyle}
                  variant={habilitado ? "success" : "danger"}
                  className="me-2 mt-2"
                  onClick={toggleHabilitado}
                >
                  {habilitado ? <><FaCheckCircle /> Habilitado</> : <><FaTimesCircle /> Deshabilitado</>}
                </Button>

                <Button
                  style={buttonStyle}
                  variant="warning"
                  className="me-2 mt-2"
                  onClick={() => {
                    // aseguramos que el modal se abra con los datos más recientes
                    setFormData({
                      Nombre: estudiante.Nombre || "",
                      ApellidoPaterno: estudiante.ApellidoPaterno || "",
                      ApellidoMaterno: estudiante.ApellidoMaterno || "",
                      FechaNacimiento: estudiante.FechaNacimiento || "",
                      CURP: estudiante.CURP || "",
                      TelefonoDeContacto: estudiante.TelefonoDeContacto || "",
                      Genero: estudiante.Genero || "",
                      Direccion: estudiante.Direccion || "",
                      Telefono: estudiante.Telefono || "",
                      Email: estudiante.Email || "",
                      Edad: estudiante.Edad || "",
                      Matricula: estudiante.Matricula || "",
                      Grado: estudiante.Grado || "",
                      Grupo: estudiante.Grupo || "",
                      Turno: estudiante.Turno || "",
                      Activo: estudiante.Activo ?? true,
                      UsuarioCreadorID: estudiante.UsuarioCreadorID || "",
                      Fotografia: estudiante.Fotografia || null,
                    });
                    setShowModal(true);
                  }}
                >
                  <FaPen /> Editar
                </Button>

                <Button
                  style={buttonStyle}
                  variant="info"
                  className="me-2 mt-2"
                  onClick={() => navigate(`/faltas/${id}`)}
                >
                  <FaListAlt /> Ver Faltas
                </Button>

                <Button
                  style={buttonStyle}
                  variant="secondary"
                  className="me-2 mt-2"
                  onClick={() => {
                    setTransferData({
                      grado: formData.Grado || estudiante.Grado || "",
                      grupo: formData.Grupo || estudiante.Grupo || "",
                      turno: formData.Turno || estudiante.Turno || "",
                    });
                    setShowTransferModal(true);
                  }}
                >
                  <FaExchangeAlt /> Transferir Alumno
                </Button>
              </div>
            </Card.Body>
          </Card>
        </Col>
      </Row>

      {/* Modal para editar estudiante (2 columnas) */}
      <Modal show={showModal} onHide={() => setShowModal(false)} size="lg">
        <Modal.Header closeButton>
          <Modal.Title>Editar Estudiante</Modal.Title>
        </Modal.Header>
        <Modal.Body>
          <Form>
            <Row>
              {[
                "Nombre",
                "ApellidoPaterno",
                "ApellidoMaterno",
                "Edad",
                "Direccion",
                "TelefonoDeContacto",
                "Email",
                "CURP",
              ].map((field) => (
                <Col md={6} key={field}>
                  <Form.Group controlId={`form${field}`} className="mb-3">
                    <Form.Label>{field}</Form.Label>
                    <Form.Control
                      type={field === "Edad" ? "number" : "text"}
                      name={field}
                      value={formData[field] ?? ""}
                      onChange={handleInputChange}
                      placeholder={`Ingrese ${field.toLowerCase()}`}
                      required={["Nombre", "ApellidoPaterno"].includes(field)}
                    />
                  </Form.Group>
                </Col>
              ))}
            </Row>
          </Form>
        </Modal.Body>
        <Modal.Footer>
          <Button variant="secondary" onClick={() => setShowModal(false)} disabled={isSaving}>
            Cancelar
          </Button>
          <Button variant="primary" onClick={handleSaveChanges} disabled={isSaving}>
            {isSaving ? "Guardando..." : "Guardar Cambios"}
          </Button>
        </Modal.Footer>
      </Modal>

      {/* Modal para transferir alumno */}
      <Modal show={showTransferModal} onHide={() => setShowTransferModal(false)} size="md">
        <Modal.Header closeButton>
          <Modal.Title>Transferir Alumno</Modal.Title>
        </Modal.Header>
        <Modal.Body>
          <Form>
            <Row>
              <Col md={12} className="mb-3">
                <Form.Group controlId="formGrado">
                  <Form.Label>Grado</Form.Label>
                  <Form.Control as="select" name="grado" value={transferData.grado} onChange={handleTransferChange}>
                    <option value="">Seleccione grado</option>
                    <option value="Primero">Primero</option>
                    <option value="Segundo">Segundo</option>
                    <option value="Tercero">Tercero</option>
                  </Form.Control>
                </Form.Group>
              </Col>

              <Col md={12} className="mb-3">
                <Form.Group controlId="formGrupo">
                  <Form.Label>Grupo</Form.Label>
                  <Form.Control as="select" name="grupo" value={transferData.grupo} onChange={handleTransferChange}>
                    <option value="">Seleccione grupo</option>
                    <option value="A">A</option>
                    <option value="B">B</option>
                    <option value="C">C</option>
                  </Form.Control>
                </Form.Group>
              </Col>

              <Col md={12} className="mb-3">
                <Form.Group controlId="formTurno">
                  <Form.Label>Turno</Form.Label>
                  <Form.Control as="select" name="turno" value={transferData.turno} onChange={handleTransferChange}>
                    <option value="">Seleccione turno</option>
                    <option value="Matutino">Matutino</option>
                    <option value="Vespertino">Vespertino</option>
                  </Form.Control>
                </Form.Group>
              </Col>
            </Row>
          </Form>
        </Modal.Body>
        <Modal.Footer>
          <Button variant="secondary" onClick={() => setShowTransferModal(false)} disabled={isTransferring}>
            Cancelar
          </Button>
          <Button variant="primary" onClick={handleTransferSubmit} disabled={isTransferring}>
            {isTransferring ? "Guardando..." : "Guardar Transferencia"}
          </Button>
        </Modal.Footer>
      </Modal>
    </Container>
  );
};

export default EstudianteDetalles;
